import React, {useEffect, useMemo, useState} from 'react';
import {envHttpUrl} from '../../http/globalMethod';

/**
 * OAuth2 客户端管理页面（移植自 lechun-mcp）
 * 接口地址已改为通过网关请求：https://scmapi.lechun.cc/lechun-mcp/admin/api/clients
 */
const OAuthClients = () => {
    const apiUrl = useMemo(() => {
        const host = `${window.location.protocol}${envHttpUrl().baseUrl}`;
        return `${host}/lechun-mcp/admin/api/clients`;
    }, []);
    const fetchOptions = useMemo(() => ({
        // 跨域请求需要带 cookie，网关才识别登录态
        credentials: 'include',
        headers: {'Accept': 'application/json'}
    }), []);

    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({type: '', text: ''});
    const [credentials, setCredentials] = useState('');
    const [editingRedirects, setEditingRedirects] = useState({});
    const [savingRedirectId, setSavingRedirectId] = useState(null);
    const [form, setForm] = useState({
        clientName: '',
        redirectUris: 'https://chat.openai.com/aip/g-xxxxxxxx/oauth/callback',
        scopes: 'openid,profile,offline_access',
        accessTokenValidityMinutes: 4320,
        refreshTokenValidityDays: 30,
        requirePkce: false,
    });

    const styleSheet = `
        .oauth-clients {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            background: #f8fafc;
            color: #0f172a;
            min-height: 100vh;
        }
        .oauth-clients header {
            background: linear-gradient(135deg, #6366f1, #8b5cf6);
            padding: 24px 32px;
            color: #fff;
        }
        .oauth-clients h1 {
            margin: 0;
            font-size: 26px;
        }
        .oauth-clients main {
            padding: 32px;
            display: grid;
            gap: 32px;
        }
        .oauth-clients .card {
            background: #fff;
            border-radius: 14px;
            box-shadow: 0 10px 30px rgba(15, 23, 42, 0.1);
            padding: 24px 28px;
        }
        .oauth-clients .card h2 {
            margin: 0 0 16px;
            font-size: 20px;
            color: #1f2937;
        }
        .oauth-clients label {
            display: block;
            font-weight: 600;
            margin-bottom: 6px;
        }
        .oauth-clients input[type="text"],
        .oauth-clients textarea,
        .oauth-clients input[type="number"] {
            width: 100%;
            border-radius: 10px;
            border: 1px solid #d1d5db;
            padding: 12px 14px;
            font-size: 15px;
            transition: border-color 0.2s, box-shadow 0.2s;
        }
        .oauth-clients textarea {
            min-height: 72px;
            resize: vertical;
        }
        .oauth-clients input:focus,
        .oauth-clients textarea:focus {
            border-color: #6366f1;
            box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
            outline: none;
        }
        .oauth-clients button {
            border: none;
            padding: 12px 18px;
            border-radius: 10px;
            font-weight: 600;
            cursor: pointer;
            background: linear-gradient(135deg, #6366f1, #8b5cf6);
            color: #fff;
            transition: transform 0.15s, box-shadow 0.15s;
        }
        .oauth-clients button:hover {
            transform: translateY(-1px);
            box-shadow: 0 12px 24px rgba(99, 102, 241, 0.2);
        }
        .oauth-clients table {
            width: 100%;
            border-collapse: collapse;
        }
        .oauth-clients th, .oauth-clients td {
            padding: 12px 14px;
            border-bottom: 1px solid #e2e8f0;
            vertical-align: top;
            text-align: left;
        }
        .oauth-clients th {
            background: #f1f5f9;
            font-weight: 700;
            font-size: 14px;
            color: #475569;
            text-transform: uppercase;
        }
        .oauth-clients .tag {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 999px;
            background: #eef2ff;
            color: #4338ca;
            font-size: 12px;
            margin: 2px 4px 2px 0;
        }
        .oauth-clients .alert {
            padding: 12px 14px;
            border-radius: 10px;
            margin-bottom: 16px;
            font-size: 14px;
        }
        .oauth-clients .alert.error {
            background: #fee2e2;
            color: #b91c1c;
            border: 1px solid #fecaca;
        }
        .oauth-clients .alert.success {
            background: #dcfce7;
            color: #166534;
            border: 1px solid #86efac;
        }
        .oauth-clients .secret {
            background: #fef3c7;
            border: 1px solid #fbbf24;
            padding: 12px 14px;
            border-radius: 10px;
            font-size: 14px;
            overflow-x: auto;
            margin: 0;
            white-space: pre-wrap;
        }
        .oauth-clients .grid {
            display: grid;
            gap: 16px;
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        }
        @media (max-width: 768px) {
            .oauth-clients main {
                padding: 16px;
            }
            .oauth-clients .card {
                padding: 20px;
            }
            .oauth-clients header {
                padding: 20px;
            }
        }
        .oauth-clients .inline-actions {
            display: flex;
            gap: 8px;
            margin-top: 8px;
        }
        .oauth-clients .ghost-button {
            background: #e0e7ff;
            color: #4338ca;
            box-shadow: none;
        }
        .oauth-clients .ghost-button:hover {
            box-shadow: none;
            transform: none;
            filter: brightness(0.98);
        }
        .oauth-clients .hint {
            color: #6b7280;
            margin: 6px 0 0;
            font-size: 12px;
        }
    `;

    const showMessage = (type, text) => setMessage({type, text});

    const loadClients = async () => {
        try {
            setLoading(true);
            const response = await fetch(apiUrl, fetchOptions);
            if (!response.ok) throw new Error('加载失败');
            const data = await response.json();
            setClients(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            showMessage('error', '加载客户端列表失败，请稍后重试。');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadClients();
    }, [apiUrl]);

    const handleInputChange = (event) => {
        const {name, value, type, checked} = event.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const startEditRedirects = (clientId, currentUris = []) => {
        const initialValue = Array.isArray(currentUris) ? currentUris.join('\n') : (currentUris || '');
        setEditingRedirects(prev => ({...prev, [clientId]: initialValue}));
    };

    const cancelEditRedirects = (clientId) => {
        setEditingRedirects(prev => {
            const {[clientId]: _, ...rest} = prev;
            return rest;
        });
    };

    const handleRedirectChange = (clientId, value) => {
        setEditingRedirects(prev => ({...prev, [clientId]: value}));
    };

    const handleSaveRedirects = async (clientId) => {
        const value = (editingRedirects[clientId] || '').trim();
        if (!value) {
            showMessage('error', 'Redirect URI 不能为空');
            return;
        }

        setSavingRedirectId(clientId);
        try {
            const response = await fetch(`${apiUrl}/${clientId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({redirectUris: value})
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.message || '更新失败，请稍后再试。');
            }

            const data = await response.json().catch(() => ({}));
            showMessage('success', data.message || 'Redirect URIs 已更新。');
            cancelEditRedirects(clientId);
            await loadClients();
        } catch (error) {
            console.error(error);
            showMessage('error', error.message || '更新失败，请检查网络或查看服务器日志。');
        } finally {
            setSavingRedirectId(null);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        showMessage('success', '正在创建客户端，请稍候...');
        setCredentials('');

        const payload = {
            clientName: form.clientName.trim(),
            redirectUris: form.redirectUris.trim(),
            scopes: form.scopes.trim(),
            accessTokenValidityMinutes: Number(form.accessTokenValidityMinutes),
            refreshTokenValidityDays: Number(form.refreshTokenValidityDays),
            requirePkce: form.requirePkce
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.message || '创建失败，请稍后重试。');
            }

            const data = await response.json();
            showMessage('success', '客户端已创建。请复制下方凭证。');
            setCredentials(`Client ID: ${data.clientId}\nClient Secret: ${data.clientSecret}`);
            await loadClients();
        } catch (error) {
            console.error(error);
            showMessage('error', error.message || '创建失败，请检查网络或查看服务器日志。');
        }
    };

    const formatList = (items = []) => {
        const list = Array.isArray(items)
            ? items
            : typeof items === 'string'
                ? items.split(/[\n,]/).map(str => str.trim()).filter(Boolean)
                : [];
        return list.map(item => (
            <span className="tag" key={item}>{item}</span>
        ));
    };

    return (
        <div className="oauth-clients">
            <style dangerouslySetInnerHTML={{__html: styleSheet}} />
            <header>
                <h1>OAuth2 客户端管理</h1>
                <p>为 ChatGPT Action 或内部应用创建、查看授权客户端。</p>
            </header>

            <main>
                <section className="card">
                    <h2>创建新客户端</h2>
                    {message.text && (
                        <div className={`alert ${message.type}`}>{message.text}</div>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div style={{marginBottom: 16}}>
                            <label htmlFor="clientName">客户端名称</label>
                            <input
                                id="clientName"
                                name="clientName"
                                type="text"
                                value={form.clientName}
                                onChange={handleInputChange}
                                placeholder="例如：ChatGPT SQL 执行动作"
                                required
                            />
                        </div>
                        <div style={{marginBottom: 16}}>
                            <label htmlFor="redirectUris">Redirect URI</label>
                            <textarea
                                id="redirectUris"
                                name="redirectUris"
                                value={form.redirectUris}
                                onChange={handleInputChange}
                                placeholder="每行一个，例如：https://chat.openai.com/aip/g-xxxxxxxx/oauth/callback"
                                required
                            />
                            <p className="hint">从自定义 GPT 的配置页面，最下方的 callback 链接复制</p>
                        </div>
                        <div style={{marginBottom: 16}}>
                            <label htmlFor="gptAuthUrl">（自定义GPT）授权 URL</label>
                            <input
                                id="gptAuthUrl"
                                type="text"
                                readOnly
                                value="https://scmapi.lechun.cc/lechun-mcp/oauth2/authorize"
                                onClick={event => event.target.select()}
                            />
                        </div>
                        <div style={{marginBottom: 16}}>
                            <label htmlFor="gptTokenUrl">（自定义GPT）令牌 URL</label>
                            <input
                                id="gptTokenUrl"
                                type="text"
                                readOnly
                                value="https://scmapi.lechun.cc/lechun-mcp/oauth2/token"
                                onClick={event => event.target.select()}
                            />
                        </div>
                        <div style={{marginBottom: 16}}>
                            <label htmlFor="scopes">Scopes</label>
                            <input
                                id="scopes"
                                name="scopes"
                                type="text"
                                value={form.scopes}
                                onChange={handleInputChange}
                            />
                            <p className="hint">逗号或换行分隔，可根据业务扩展自定义 scope。</p>
                        </div>
                        <div className="grid" style={{marginBottom: 16}}>
                            <div>
                                <label htmlFor="accessTokenValidityMinutes">Access Token 有效期（分钟）</label>
                                <input
                                    id="accessTokenValidityMinutes"
                                    name="accessTokenValidityMinutes"
                                    type="number"
                                    min="1"
                                    value={form.accessTokenValidityMinutes}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div>
                                <label htmlFor="refreshTokenValidityDays">Refresh Token 有效期（天）</label>
                                <input
                                    id="refreshTokenValidityDays"
                                    name="refreshTokenValidityDays"
                                    type="number"
                                    min="1"
                                    value={form.refreshTokenValidityDays}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div style={{marginBottom: 16}}>
                            <label style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                <input
                                    id="requirePkce"
                                    name="requirePkce"
                                    type="checkbox"
                                    checked={form.requirePkce}
                                    disabled
                                    onChange={handleInputChange}
                                />
                                不强制使用 PKCE（创建 GPT 请不要勾选）
                            </label>
                            <p className="hint">后端默认关闭 PKCE，创建自定义 GPT 时保持未勾选。</p>
                        </div>
                        <button type="submit">创建客户端</button>
                    </form>

                    {credentials && (
                        <div style={{marginTop: 24}}>
                            <div className="alert success">
                                客户端创建成功！请立即保存以下凭证，密钥只会显示一次。
                            </div>
                            <pre className="secret">{credentials}</pre>
                        </div>
                    )}
                </section>

                <section className="card">
                    <h2>已注册客户端</h2>
                    {loading ? (
                        <p>正在加载...</p>
                    ) : (
                        <table>
                            <thead>
                            <tr>
                                <th>名称</th>
                                <th>Client ID</th>
                                <th>Redirect URIs</th>
                                <th>Scopes</th>
                                <th>授权方式</th>
                                <th>创建时间</th>
                            </tr>
                            </thead>
                            <tbody>
                            {clients.map(client => (
                                <tr key={client.clientId}>
                                    <td>{client.clientName}</td>
                                    <td><code>{client.clientId}</code></td>
                                    <td>
                                        {editingRedirects[client.clientId] !== undefined ? (
                                            <>
                                                <textarea
                                                    value={editingRedirects[client.clientId]}
                                                    onChange={event => handleRedirectChange(client.clientId, event.target.value)}
                                                    placeholder="每行一个 Redirect URI"
                                                />
                                                <div className="inline-actions">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveRedirects(client.clientId)}
                                                        disabled={savingRedirectId === client.clientId}
                                                    >
                                                        {savingRedirectId === client.clientId ? '保存中...' : '保存'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="ghost-button"
                                                        onClick={() => cancelEditRedirects(client.clientId)}
                                                        disabled={savingRedirectId === client.clientId}
                                                    >
                                                        取消
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {formatList(client.redirectUris || [])}
                                                <div className="inline-actions">
                                                    <button
                                                        type="button"
                                                        className="ghost-button"
                                                        onClick={() => startEditRedirects(client.clientId, client.redirectUris)}
                                                    >
                                                        编辑
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </td>
                                    <td>{formatList(client.scopes || [])}</td>
                                    <td>{formatList(client.grantTypes || [])}</td>
                                    <td>{client.clientIdIssuedAt ? new Date(client.clientIdIssuedAt).toLocaleString() : '-'}</td>
                                </tr>
                            ))}
                            {clients.length === 0 && (
                                <tr>
                                    <td colSpan="6">暂无数据</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    )}
                </section>
            </main>
        </div>
    );
};

export default OAuthClients;
